// src/pages/Settings/Billing/intro/index.js
//
// The Billing tab landing page (/settings/4/0). This used to be a centered
// placeholder ("Welcome to the hub of company configuration…"). It is now a
// real overview of the whole billing area: licence spend, invoices, saved
// cards, upcoming renewals. Every box is clickable and drills into the matching
// Billing sub-panel.
//
// It reuses the same Redux data the individual Billing panels load, so no new
// API work is needed. `setActiveTab` is passed down from Settings/index.js and
// lets each box jump straight to the matching sub-panel.

import React, { useEffect, useMemo } from "react";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import CountUp from "react-countup";

import SettingsPanel from "../../components/SettingsPanel";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import handleDateTimeLeft from "../../../../Components/Common/HandleDateTimeLeft";

import { getLicensesSubscriptionList } from "../../../../store/manageLicense/action";
import { getMasterInvoiceListCustomer } from "../../../../store/invoiceCrmIT/action";
import { getCustomerCards } from "../../../../store/charges/action";
import { getCompanyTeamMembersList } from "../../../../store/companyToUser/action";

import amex from "../../../../assets/images/creditDebitCards/amex.png";
import anyCard from "../../../../assets/images/creditDebitCards/anyCard.png";
import masterCard from "../../../../assets/images/creditDebitCards/mastercard.png";
import visa from "../../../../assets/images/creditDebitCards/visa.png";

// Billing sub-panel ids, mirroring settingsContentDataBilling.
const PANEL = {
  INVOICES: 1,
  LICENSES: 2,
  CARDS: 3,
  USERS: 6,
};

const cardArtwork = (brand) => {
  switch (brand) {
    case "visa":
      return visa;
    case "masterCard":
      return masterCard;
    case "American Express":
      return amex;
    default:
      return anyCard;
  }
};

// Is an admin (SecurityAuthRole 100)? Gates drilling into the Users panel,
// exactly as settingsNavDataBilling gates its own nav entries.
const isAdministrator = () =>
  !!JSON.parse(localStorage.getItem("authUser"))
    ?.get_securityauthrole_list?.find(
      (item) => item.SecurityAuthRole_Id === "100"
    );

// A licence counts towards live spend if it has no end date, or an end date in
// the future. Mirrors the logic in manageLicense/summary.js.
const isLive = (licence) =>
  !licence?.EndDateTime || new Date(licence.EndDateTime) > new Date();

// Props that turn any Card into a keyboard-accessible clickable tile.
const tileProps = (onClick) =>
  onClick
    ? {
        onClick,
        role: "button",
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};

function StatCard({ label, icon, value, prefix = "", decimals = 0, tone, hint, onClick }) {
  return (
    <Col xs={12} sm={6} xl={4}>
      <Card
        {...tileProps(onClick)}
        className={`h-100 mb-0 ${onClick ? "billing-tile" : ""}`}
      >
        <CardBody>
          <div className="d-flex align-items-center mb-2">
            <p className="text-uppercase fw-medium text-muted mb-0 fs-12 flex-grow-1">
              {label}
            </p>
            <i className={`${icon} fs-20 ${tone || "text-muted"}`} />
          </div>
          <h3 className={`mb-1 ${tone || ""}`}>
            <CountUp
              start={0}
              end={Number(value) || 0}
              prefix={prefix}
              decimals={decimals}
              duration={1.5}
              separator=","
            />
          </h3>
          {hint && <p className="text-muted mb-0 fs-12">{hint}</p>}
        </CardBody>
      </Card>
    </Col>
  );
}

// A card header with a title and a "drill in" chevron, so it reads as clickable.
function TileHeader({ title }) {
  return (
    <CardHeader className="d-flex align-items-center">
      <h5 className="card-title mb-0 flex-grow-1">{title}</h5>
      <i className="ri-arrow-right-line text-muted fs-18" />
    </CardHeader>
  );
}

export default function BillingSettingsIntro({ setActiveTab }) {
  const dispatch = useDispatch();
  const admin = useMemo(() => isAdministrator(), []);

  const licences = useSelector(
    (state) =>
      state.ManageLicense.licensesSubscriptionList?.subscriptionlicence_list
  );
  const invoices = useSelector(
    (state) => state.InvoiceCrmIT.masterInvoiceListCustomer?.invoice_list
  );
  const cards = useSelector(
    (state) => state.Charges.customerCardsList?.masterpaymentcard_list
  );
  const teamMembers = useSelector(
    (state) => state.CompanyToUser.companyTeamMembersList?.companytouser_list
  );

  useEffect(() => {
    dispatch(getLicensesSubscriptionList());
    dispatch(getMasterInvoiceListCustomer());
    dispatch(getCustomerCards());
    dispatch(getCompanyTeamMembersList());
  }, [dispatch]);

  // --- Derived figures -----------------------------------------------------
  const stats = useMemo(() => {
    const list = licences || [];
    const assigned = list.filter((l) => l?.User_Id).length;

    const sumByCycle = (cycle) =>
      list
        .filter(
          (l) => l?.SubscriptionLicenceDurationType_Name === cycle && isLive(l)
        )
        .reduce((total, l) => total + Number(l?.AmountLocalSubTotal || 0), 0);

    const outstanding = (invoices || []).reduce(
      (total, inv) => total + Number(inv?.AmountUnCleared || 0),
      0
    );

    return {
      totalLicences: list.length,
      assigned,
      unassigned: list.length - assigned,
      monthlyCost: sumByCycle("Monthly"),
      yearlyCost: sumByCycle("Yearly"),
      outstanding,
      cardCount: (cards || []).length,
      teamCount: (teamMembers || []).length,
    };
  }, [licences, invoices, cards, teamMembers]);

  const recentInvoices = useMemo(
    () =>
      [...(invoices || [])]
        .sort(
          (a, b) => new Date(b?.InvoiceDateTime) - new Date(a?.InvoiceDateTime)
        )
        .slice(0, 5),
    [invoices]
  );

  const upcomingRenewals = useMemo(
    () =>
      (licences || [])
        .filter(
          (l) => l?.RenewalDateTime && new Date(l.RenewalDateTime) > new Date()
        )
        .sort(
          (a, b) => new Date(a?.RenewalDateTime) - new Date(b?.RenewalDateTime)
        )
        .slice(0, 5),
    [licences]
  );

  const primaryCard = useMemo(
    () =>
      (cards || []).find((c) => c?.FlagPrimary === "1") || (cards || [])[0],
    [cards]
  );

  const go = (id) => () => setActiveTab && setActiveTab(id);

  return (
    <SettingsPanel
      title="Billing Overview"
      description="A snapshot of your CRMit subscription: licences, invoices, cards and upcoming renewals."
    >
      {/* KPI row — each box drills into its panel */}
      <Row className="g-3">
        <StatCard
          label="Active Licences"
          icon="fa-regular fa-id-badge"
          value={stats.totalLicences}
          hint={`${stats.assigned} assigned · ${stats.unassigned} free`}
          onClick={go(PANEL.LICENSES)}
        />
        <StatCard
          label="Monthly Spend"
          icon="fa-solid fa-calendar-check"
          value={stats.monthlyCost}
          prefix="£"
          decimals={2}
          hint="Recurring monthly"
          onClick={go(PANEL.LICENSES)}
        />
        <StatCard
          label="Yearly Spend"
          icon="ri-exchange-funds-line"
          value={stats.yearlyCost}
          prefix="£"
          decimals={2}
          hint="Recurring yearly"
          onClick={go(PANEL.LICENSES)}
        />
        <StatCard
          label="Outstanding"
          icon="fa-solid fa-triangle-exclamation"
          value={stats.outstanding}
          prefix="£"
          decimals={2}
          tone={stats.outstanding > 0 ? "text-danger" : "text-success"}
          hint={stats.outstanding > 0 ? "Unpaid on invoices" : "All paid up"}
          onClick={go(PANEL.INVOICES)}
        />
        <StatCard
          label="Saved Cards"
          icon="fa-solid fa-credit-card"
          value={stats.cardCount}
          hint={
            primaryCard ? `•••• ${primaryCard.Last4Digits}` : "No cards on file"
          }
          onClick={go(PANEL.CARDS)}
        />
        <StatCard
          label="Team Members"
          icon="fa-solid fa-users"
          value={stats.teamCount}
          hint="Users in this company"
          onClick={admin ? go(PANEL.USERS) : undefined}
        />
      </Row>

      {/* Invoices (left) + renewals & payment method (right) */}
      <Row className="g-3 mt-1">
        <Col xl={8}>
          <Card
            {...tileProps(go(PANEL.INVOICES))}
            className="mb-0 h-100 billing-tile"
          >
            <TileHeader title="Recent Invoices" />
            <CardBody className="pt-0">
              {isEmpty(recentInvoices) ? (
                <div className="text-muted text-center py-4">
                  No invoices yet.
                </div>
              ) : (
                <div className="vstack gap-1">
                  {recentInvoices.map((inv) => (
                    <div
                      key={inv?.Id}
                      className="d-flex align-items-center gap-2 py-2 border-bottom border-bottom-dashed"
                    >
                      <div className="flex-grow-1 min-w-0">
                        <span className="fw-medium">#{inv?.Id}</span>
                        {inv?.Title && (
                          <span className="text-muted ms-2 fs-13 text-truncate">
                            {inv.Title}
                          </span>
                        )}
                        <div className="text-muted fs-12">
                          {HandleDateTime(inv?.InvoiceDateTime)}
                        </div>
                      </div>
                      <div className="text-end flex-shrink-0">
                        <div className="fw-medium">
                          £{inv?.AmountLocalTotal}
                        </div>
                        <span
                          className="badge text-uppercase"
                          style={{
                            background:
                              inv?.InvoiceStatus_CssColourBackground,
                            color: inv?.InvoiceStatus_CssColourText,
                          }}
                        >
                          {inv?.InvoiceStatus_Name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col xl={4}>
          <div className="vstack gap-3 h-100">
            <Card
              {...tileProps(go(PANEL.LICENSES))}
              className="mb-0 billing-tile"
            >
              <TileHeader title="Upcoming Renewals" />
              <CardBody className="pt-0">
                {isEmpty(upcomingRenewals) ? (
                  <div className="text-muted text-center py-4">
                    No renewals scheduled.
                  </div>
                ) : (
                  <div className="vstack gap-3">
                    {upcomingRenewals.map((l) => (
                      <div
                        key={l?.Id}
                        className="d-flex align-items-center gap-2"
                      >
                        <span className="avatar-xs flex-shrink-0">
                          <span className="avatar-title bg-soft-primary text-primary rounded">
                            <i className="fa-regular fa-id-badge" />
                          </span>
                        </span>
                        <div className="flex-grow-1 min-w-0">
                          <span className="fw-medium text-truncate d-block">
                            {l?.SubscriptionProduct_Name || "Licence"}
                          </span>
                          <div className="text-muted fs-12">
                            {l?.User_Id ? "Assigned" : "Unassigned"}
                          </div>
                        </div>
                        <div className="text-muted fs-12 flex-shrink-0">
                          {handleDateTimeLeft(l?.RenewalDateTime)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>

            <Card
              {...tileProps(go(PANEL.CARDS))}
              className="mb-0 billing-tile"
            >
              <TileHeader title="Payment Method" />
              <CardBody className="pt-0">
                {primaryCard ? (
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={cardArtwork(primaryCard?.Brand)}
                      alt={primaryCard?.Brand}
                      width="44"
                    />
                    <div className="min-w-0">
                      <div className="fw-medium">
                        •••• {primaryCard?.Last4Digits}
                        {primaryCard?.FlagPrimary === "1" && (
                          <span className="ms-2 badge badge-soft-success">
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="text-muted fs-12">
                        Expires{" "}
                        {String(primaryCard?.ExpiryMonth).padStart(2, "0")}/
                        {primaryCard?.ExpiryYear}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted text-center py-4">
                    No cards saved yet.
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </Col>
      </Row>
    </SettingsPanel>
  );
}
